import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Depth from '@/assets/icons/depth.svg?component';
import Oxygen from '@/assets/icons/oxygen.svg?component';
import Salinity from '@/assets/icons/salinity.svg?component';
import Temperature from '@/assets/icons/temperature.svg?component';
import TrendNormal from '@/assets/icons/trend_normal.svg?component';
import InfoTip from '@/components/atoms/InfoTip';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import Tooltip from '@/components/atoms/Tooltip';
import MetricCard, { type MetricCardBadge } from '@/components/molecules/MetricCard/MetricCard';
import { formatWallClockWorded } from '@/utils/date';
import type { Locale } from '@/utils/localeFormatters';
import {
    formatPercentageByLocale,
    formatTemperatureByLocale,
    formatUniversalUnitByLocale,
    parseLocale,
} from '@/utils/localeFormatters';

import styles from './EnvironmentalDataCard.module.scss';

const OXYGEN_CONCENTRATION_THRESHOLD_DEFAULT = {
    /** Exclusive: tooltip is "optimal > 6 mg/L". */
    optimal: 6,
    /** Inclusive lower bound: tooltip is "sub-optimal 4–6 mg/L". */
    suboptimal: 4,
} as const;

export interface EnvironmentalDataCardProps {
    isLoading?: boolean;
    oxygenConcentration?: number;
    oxygenSaturation?: number;
    temperature?: number;
    depth?: number;
    salinity?: number;
    /**
     * The site's wall clock (`YYYY-MM-DDTHH:mm:ss`, no zone), shown as it is. Absent while the
     * card stands in for data that is still loading.
     */
    lastMeasurement?: string;
}

export function EnvironmentalDataCard(props: EnvironmentalDataCardProps) {
    const {
        isLoading = false,
        oxygenSaturation,
        oxygenConcentration,
        temperature,
        depth,
        salinity,
        lastMeasurement,
    } = props;

    const { t, i18n } = useTranslation('environmentalData');
    const locale = parseLocale(i18n.language);

    const hasOxygenData = oxygenSaturation != null && oxygenConcentration != null;
    const hasSalinityData = salinity != null;

    const oxygenValueContent = formatOxygenValue(oxygenSaturation, oxygenConcentration, locale);
    const oxygenBadge = useMemo(
        () => resolveOxygenBadge(oxygenConcentration, hasOxygenData, t),
        [oxygenConcentration, hasOxygenData, t]
    );

    return (
        <div className={styles.container}>
            <div className={styles.title}>
                <span>{t('card.title')}</span>
                <InfoTip topic={t('card.title')} content={t('tooltips.environmentalData')} />
            </div>
            <div className={styles.subtitle}>
                <span>{t('card.lastMeasurement')}</span>
                {isLoading ? (
                    <SkeletonBox
                        width={150}
                        height={24}
                        borderRadius={8}
                        className={styles.skeleton}
                    />
                ) : (
                    <span>{formatWallClockWorded(lastMeasurement)}</span>
                )}
            </div>
            <div className={styles.content}>
                {hasOxygenData && (
                    <Tooltip content={t('tooltips.oxygenLevel')}>
                        <MetricCard
                            isLoading={isLoading}
                            className={styles.metricCard}
                            title={t('card.oxygen')}
                            value={oxygenValueContent}
                            icon={Oxygen}
                            tone="info"
                            badge={oxygenBadge}
                            iconCustomSize={18}
                        />
                    </Tooltip>
                )}
                {hasSalinityData && (
                    <MetricCard
                        isLoading={isLoading}
                        className={styles.metricCard}
                        title={t('card.salinity')}
                        value={formatUniversalUnitByLocale(salinity, 'PSU', locale)}
                        icon={Salinity}
                        tone="neutral"
                    />
                )}
                <MetricCard
                    isLoading={isLoading}
                    className={styles.metricCard}
                    title={t('card.temperature')}
                    value={formatTemperatureByLocale(temperature, '°C', locale)}
                    icon={Temperature}
                    tone="success"
                />
                <Tooltip content={t('tooltips.averageDepth')}>
                    <MetricCard
                        isLoading={isLoading}
                        className={styles.metricCard}
                        title={t('card.averageDepth')}
                        value={formatUniversalUnitByLocale(depth, 'm', locale)}
                        icon={Depth}
                        tone="neutral"
                    />
                </Tooltip>
            </div>
        </div>
    );
}

function resolveOxygenBadge(
    oxygenConcentration: number | undefined,
    hasOxygenData: boolean,
    t: (key: string) => string
): MetricCardBadge | undefined {
    if (!hasOxygenData || oxygenConcentration == null) {
        return undefined;
    }
    // Matches tooltips.oxygenLevel: optimal > 6, sub-optimal 4–6, low < 4.
    if (oxygenConcentration > OXYGEN_CONCENTRATION_THRESHOLD_DEFAULT.optimal) {
        return {
            icon: TrendNormal,
            label: t('card.optimal'),
            tone: 'success',
        };
    }
    if (oxygenConcentration >= OXYGEN_CONCENTRATION_THRESHOLD_DEFAULT.suboptimal) {
        return {
            icon: TrendNormal,
            label: t('card.suboptimal'),
            tone: 'caution',
        };
    }
    return {
        icon: TrendNormal,
        label: t('card.low'),
        tone: 'danger',
    };
}

function formatOxygenValue(
    oxygenSaturation: number | undefined,
    oxygenConcentration: number | undefined,
    locale: Locale
): ReactNode {
    if (oxygenSaturation == null || oxygenConcentration == null) {
        return <span>-</span>;
    }
    return (
        <span className={styles.oxygenLevel}>
            {`${formatPercentageByLocale(oxygenSaturation, locale)}, ${formatUniversalUnitByLocale(oxygenConcentration, 'mg/L', locale)}`}
        </span>
    );
}
